import { HttpClient, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable, InjectionToken, Injector, Optional } from '@angular/core';
import { Observable } from 'rxjs';
import { AcceptLanguageInterceptor } from './accept-language.interceptor';

import { ApiPrefixInterceptor } from './api-prefix.interceptor';
import { AuthorizationInterceptor } from './authorization.interceptor';
import { BasicAuthorizationInterceptor } from './basic-authorization.interceptor';
import { ErrorHandlerInterceptor } from './error-handler.interceptor';
import { CacheInterceptor, CacheSettings } from 'projects/shared/src/lib/shared/core/http/cache.interceptor';
import { AccessTokenInterceptor } from 'projects/shared/src/lib/shared/core/http/access-token.interceptor';
import { HttpCacheService } from 'projects/shared/src/lib/shared/core/http/http-cache.service';

// HttpClient is declared in a re-exported module, so we have to extend the original module to make it work properly
// (see https://github.com/Microsoft/TypeScript/issues/13897)
declare module '@angular/common/http' {
  // Augment HttpClient with the added configuration methods from HttpService, to allow in-place replacement of
  // HttpClient with HttpService using dependency injection
  export interface HttpClient {
    /**
     * Enables caching for this request.
     * @param options Adds options to the cache interceptor
     * @return The new instance.
     */
    cache(options?: CacheSettings, removeCache?: boolean): HttpClient;

    /**
     * Skips default error handler for this request.
     * @return The new instance.
     */
    skipErrorHandler(): HttpClient;

    /**
     * Do not use API prefix for this request.
     * @return The new instance.
     */
    disableApiPrefix(): HttpClient;

    /**
     * Do not use the access token for this request.
     * @returns The new instance.
     */
    disableAccessToken(): HttpClient;

    /**
     * Do not use the ngx loading bar service for this request.
     * @returns The new instance.
     */
    disableLoading(): HttpClient;

    /**
     * Do not use the autorization interceptor for this request
     * @returns The new instance.
     */
    disableAuthorizationInterceptor(): HttpClient;


    /**
     * Do not use the accepted language interceptor for this request
     * @returns The new instance.
     */
    disableAcceptedLanguage(): HttpClient;

    /**
     * Do not use the Country CODE interceptor for this request
     * @returns The new instance.
     */
    disableCountryCode(): HttpClient;
  }
}

// From @angular/common/http/src/interceptor: allows to chain interceptors
class HttpInterceptorHandler implements HttpHandler {
  // -----------------------------------------------------------------------------------------------------
  // @ CONSTRUCTOR
  // -----------------------------------------------------------------------------------------------------

  constructor(private next: HttpHandler, private interceptor: HttpInterceptor) { }

  // -----------------------------------------------------------------------------------------------------
  // @ INTERFACES
  // -----------------------------------------------------------------------------------------------------

  handle(request: HttpRequest<any>): Observable<HttpEvent<any>> {
    return this.interceptor.intercept(request, this.next);
  }
}

/**
 * Allows to override default dynamic interceptors that can be disabled with the HttpService extension.
 * Except for very specific needs, you should better configure these interceptors directly in the constructor below
 * for better readability.
 *
 * For static interceptors that should always be enabled (like ApiPrefixInterceptor), use the standard
 * HTTP_INTERCEPTORS token.
 */
export const HTTP_DYNAMIC_INTERCEPTORS = new InjectionToken<HttpInterceptor>('HTTP_DYNAMIC_INTERCEPTORS');

/**
 * Extends HttpClient with per request configuration using dynamic interceptors.
 */
@Injectable()
export class HttpService extends HttpClient {
  constructor(
    private httpHandler: HttpHandler,
    private injector: Injector,
    @Optional() @Inject(HTTP_DYNAMIC_INTERCEPTORS) private interceptors: HttpInterceptor[] = []
  ) {
    super(httpHandler);

    if (!this.interceptors) {
      // Configure default interceptors that can be disabled here
      this.interceptors = [
        this.injector.get(ApiPrefixInterceptor),
        this.injector.get(ErrorHandlerInterceptor),
        this.injector.get(AccessTokenInterceptor),
        this.injector.get(AuthorizationInterceptor),
        this.injector.get(AcceptLanguageInterceptor)
      ];
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ INTERFACES
  // -----------------------------------------------------------------------------------------------------

  cache(options?: CacheSettings, removeCache?: boolean): HttpClient {
    if (removeCache === true) {
      const httpCacheService = this.injector.get(HttpCacheService);
      httpCacheService.cleanCache();
      return this;
    } else {
      const cacheInterceptor = this.injector.get(CacheInterceptor).configure(options);
      return this.addInterceptor(cacheInterceptor);
    }
  }

  skipErrorHandler(): HttpService {
    return this.removeInterceptor(ErrorHandlerInterceptor);
  }

  disableApiPrefix(): HttpService {
    return this.removeInterceptor(ApiPrefixInterceptor);
  }

  disableAccessToken(): HttpService {
    return this.removeInterceptor(AccessTokenInterceptor);
  }

  disableAcceptedLanguage(): HttpService {
    return this.removeInterceptor(AcceptLanguageInterceptor);
  }

  disableAuthorizationInterceptor(): HttpService {
    return this.removeInterceptor(AuthorizationInterceptor);
  }

  // Override the original method to wire interceptors when triggering the request.
  request(method?: any, url?: any, options?: any): any {
    const handler = this.interceptors.reduceRight(
      (next, interceptor) => new HttpInterceptorHandler(next, interceptor),
      this.httpHandler
    );
    return new HttpClient(handler).request(method, url, options);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ PRIVATE METHODS
  // -----------------------------------------------------------------------------------------------------

  private removeInterceptor(interceptorType: any): HttpService {
    return new HttpService(
      this.httpHandler,
      this.injector,
      this.interceptors.filter(i => !(i instanceof interceptorType))
    );
  }

  private addInterceptor(interceptor: HttpInterceptor): HttpService {
    return new HttpService(this.httpHandler, this.injector, this.interceptors.concat([interceptor]));
  }
}
